import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Translator } from './translator';

const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";

function getOrCreateDeviceId() {
  let id = localStorage.getItem("teche_device_id");
  if (!id) {
    const rnd =
      crypto.randomUUID?.() ||
      ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4)).toString(16)
      );
    localStorage.setItem("teche_device_id", rnd);
    id = rnd;
  }
  return id;
}

const schema = yup.object().shape({
  fullName: yup
    .string()
    .min(2, 'Il nome deve contenere almeno 2 caratteri')
    .matches(/^[a-zA-Z\s]+$/, 'Nome e cognome devono contenere solo lettere')
    .required('Nome e cognome sono obbligatori'),
  email: yup.string().email('Email non valida').required('Email obbligatoria'),
  password: yup
    .string()
    .min(6, 'La password deve contenere almeno 6 caratteri')
    .required('La password è obbligatoria'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Le password devono coincidere')
    .required('Per favore, reinserisci la password'),
  accettoprivacy: yup
    .boolean()
    .oneOf([true], 'Devi accettare i termini e le condizioni'),
});

const Register = () => {
  const { translateText, lang } = Translator();

  // --- Fixed IT / EN / FR ----------------------------------------------------
  const baseTextByLang = useMemo(
    () => ({
      it: {
        heading: "Crea un account",
        fullNameLabel: "Nome e cognome:",
        fullNamePlaceholder: "Inserisci nome e cognome",
        emailLabel: "Email:",
        emailPlaceholder: "Inserisci la tua email",
        passwordLabel: "Password:",
        passwordPlaceholder: "Almeno 6 caratteri",
        confirmPasswordLabel: "Conferma password:",
        confirmPasswordPlaceholder: "Reinserisci la tua password",
        verificationCodePlaceholder: "Inserisci il codice di verifica",
        registerButton: "Registrati",
        legalLine1: "Creando un account, accetti i termini di Te.C.H-e",
        legalLine2: "Condizioni d'uso",
        legalLine3: "e",
        legalLine4: "Informativa sulla privacy",
        alreadyHaveAccount: "Hai già un account?",
        loginLink: "Accedi",
        text1: "Registrazione avvenuta con successo!\n Benvenuti su Te.C.H-e",
        text2: "! Effettua l'accesso con le tue nuove credenziali!",
        text3: "Password non corrispondente. Controlla la password.",
        text4: "L'indirizzo email esiste già. Prova con un altro indirizzo.",
        text5: "Si è verificato un errore imprevisto.",
        text6: "Si è verificato un errore. Riprova più tardi.",
        alertEmailExistsLogin: "Questa e-mail è già registrata. Effettua il login.",
        alertCodeSent:
          "Un codice di verifica è stato inviato alla tua email. Controlla la tua casella di posta.",
        alertCodeFailed:
          "Impossibile inviare il codice di verifica. Riprova più tardi.",
      },

      en: {
        heading: "Create an account",
        fullNameLabel: "Full name:",
        fullNamePlaceholder: "Enter your first and last name",
        emailLabel: "Email:",
        emailPlaceholder: "Enter your email address",
        passwordLabel: "Password:",
        passwordPlaceholder: "At least 6 characters",
        confirmPasswordLabel: "Confirm password:",
        confirmPasswordPlaceholder: "Re-enter your password",
        verificationCodePlaceholder: "Enter the verification code",
        registerButton: "Sign up",
        legalLine1: "By creating an account, you agree to Te.C.H-e's",
        legalLine2: "Terms of Use",
        legalLine3: "and",
        legalLine4: "Privacy Policy",
        alreadyHaveAccount: "Already have an account?",
        loginLink: "Log in",
        text1: "Registration completed successfully!\n Welcome to Te.C.H-e",
        text2: "! Log in with your new credentials!",
        text3: "Password mismatch. Please check your password.",
        text4: "This email address already exists. Please try another.",
        text5: "An unexpected error occurred.",
        text6: "An error occurred. Please try again later.",
        alertEmailExistsLogin: "This email is already registered. Please log in.",
        alertCodeSent:
          "A verification code has been sent to your email. Please check your inbox.",
        alertCodeFailed:
          "Unable to send the verification code. Please try again later.",
      },

      fr: {
        heading: "Créer un compte",
        fullNameLabel: "Nom et prénom :",
        fullNamePlaceholder: "Saisissez votre nom et prénom",
        emailLabel: "E-mail :",
        emailPlaceholder: "Saisissez votre adresse e-mail",
        passwordLabel: "Mot de passe :",
        passwordPlaceholder: "Au moins 6 caractères",
        confirmPasswordLabel: "Confirmez le mot de passe :",
        confirmPasswordPlaceholder: "Saisissez à nouveau votre mot de passe",
        verificationCodePlaceholder: "Saisissez le code de vérification",
        registerButton: "S’inscrire",
        legalLine1:
          "En créant un compte, vous acceptez les conditions de Te.C.H-e",
        legalLine2: "Conditions d’utilisation",
        legalLine3: "et la",
        legalLine4: "Politique de confidentialité",
        alreadyHaveAccount: "Vous avez déjà un compte ?",
        loginLink: "Se connecter",
        text1: "Inscription réussie !\n Bienvenue sur Te.C.H-e",
        text2: "! Connectez-vous avec vos nouveaux identifiants !",
        text3:
          "Les mots de passe ne correspondent pas. Vérifiez votre mot de passe.",
        text4:
          "Cette adresse e-mail existe déjà. Veuillez en utiliser une autre.",
        text5: "Une erreur inattendue s’est produite.",
        text6: "Une erreur s’est produite. Veuillez réessayer plus tard.",
        alertEmailExistsLogin:
          "Cet e-mail est déjà enregistré. Veuillez vous connecter.",
        alertCodeSent:
          "Un code de vérification a été envoyé à votre e-mail. Vérifiez votre boîte de réception.",
        alertCodeFailed:
          "Impossible d’envoyer le code de vérification. Veuillez réessayer plus tard.",
      },
    }),
    []
  );

  const [translatedText, setTranslatedText] = useState(
    baseTextByLang[lang] || baseTextByLang.en
  );
  const [selectedPlan, setSelectedPlan] = useState("BASIC");

  // Read plan from URL / session
  useEffect(() => {
    const urlPlan = new URLSearchParams(window.location.search).get("plan");
    const stored = sessionStorage.getItem("selected_plan");
    const p = (urlPlan || stored || "BASIC").toUpperCase();
    setSelectedPlan(p);
  }, []);

  // i18n: IT/EN/FR fixed; other languages via Translator (starting from Italian)
  useEffect(() => {
    // Fixed strings for it/en/fr
    if (lang === "it" || lang === "en" || lang === "fr") {
      setTranslatedText(baseTextByLang[lang] || baseTextByLang.en);
      return;
    }

    // Other languages → auto-translate from Italian
    let cancelled = false;
    const translateAll = async () => {
      try {
        const source = baseTextByLang.it;
        const entries = Object.entries(source);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            const out = await translateText(value);
            return [key, out || value];
          })
        );
        if (!cancelled) {
          setTranslatedText(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setTranslatedText(baseTextByLang.en);
        }
      }
    };
    translateAll();
    return () => {
      cancelled = true;
    };
  }, [lang, translateText, baseTextByLang]);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  const [showVerificationField, setShowVerificationField] = useState(false);

  const checkEmailExists = async (email) => {
    try {
      const response = await fetch(
        `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/getUserIDOLD?email=${encodeURIComponent(
          email
        )}`
      );
      const data = await response.json();
      return data.length > 0;
    } catch (error) {
      console.error("Error checking email:", error);
      return false;
    }
  };

  const sendVerificationCode = async (email) => {
    try {
      const response = await fetch(
        `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/sendVerificationCode?email=${encodeURIComponent(
          email
        )}`
      );
      const result = await response.json();
      return result.trim() === "1";
    } catch (error) {
      console.error("Error sending verification code:", error);
      return false;
    }
  };

  const verifyCode = async (email, code) => {
    try {
      const response = await fetch(
        `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/verifyCode?email=${encodeURIComponent(
          email
        )}&code=${encodeURIComponent(code)}`
      );
      const result = await response.json();
      return result === "1";
    } catch (error) {
      console.error("Error verifying code:", error);
      return false;
    }
  };

  const onSubmit = async (data) => {
    if (!showVerificationField) {
      const emailExists = await checkEmailExists(data.email);
      if (emailExists) {
        alert(translatedText.alertEmailExistsLogin);
        window.location.href = "/login";
        return;
      }

      const codeSent = await sendVerificationCode(data.email);
      if (codeSent) {
        alert(translatedText.alertCodeSent);
        setShowVerificationField(true);
      } else {
        alert(translatedText.alertCodeFailed);
      }
    } else {
      const codeVerified = await verifyCode(data.email, data.verificationCode);
      if (codeVerified) {
        const nameParts = data.fullName.trim().split(" ");
        const firstName = nameParts[0];
        const lastName = nameParts.slice(1).join(" ") || "N/A";
        try {
          const params = new URLSearchParams({
            firstName,
            lastName,
            email: data.email,
            uName: data.fullName,
            password: data.password,
            password2: data.confirmPassword,
            accettoprivacy: "Yes",
            opt1: 0,
            opt2: 0,
          });
          const sel = (selectedPlan || "").toUpperCase();
          if (sel === "BASIC" || sel === "PLUS") {
            params.append("plan", sel);
          }
          const response = await fetch(
            `${BASE}/setNewUser?${params.toString()}`,
            { method: "GET" }
          );
          const result = await response.text();
          if (result === "1") {
            alert(`${translatedText.text1} ${data.fullName}${translatedText.text2}`);
            window.location.href = "/login";
          } else if (result === "0") {
            alert(translatedText.text3);
          } else if (result === "-1") {
            alert(translatedText.text4);
          } else {
            alert(translatedText.text5);
          }
        } catch (error) {
          console.error("Error registering user:", error);
          alert(translatedText.text6);
        }
      }
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-100 p-4">
      <img
        src="/LOGO TECHE letters.png"
        alt="Logo"
        className="w-40 h-auto mb-4"
      />
      <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition">
        <h2 className="text-xl font-bold text-black mb-6 text-center">
          {translatedText.heading}
        </h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-black">
              {translatedText.fullNameLabel}
            </label>
            <input
              type="text"
              {...register("fullName")}
              placeholder={translatedText.fullNamePlaceholder}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-red-600 text-sm font-semibold">
              {errors.fullName?.message}
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-black">
              {translatedText.emailLabel}
            </label>
            <input
              type="email"
              {...register("email")}
              placeholder={translatedText.emailPlaceholder}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-red-600 text-sm font-semibold">
              {errors.email?.message}
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-black">
              {translatedText.passwordLabel}
            </label>
            <input
              type="password"
              {...register("password")}
              placeholder={translatedText.passwordPlaceholder}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-red-600 text-sm font-semibold">
              {errors.password?.message}
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-black">
              {translatedText.confirmPasswordLabel}
            </label>
            <input
              type="password"
              {...register("confirmPassword")}
              placeholder={translatedText.confirmPasswordPlaceholder}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-red-600 text-sm font-semibold">
              {errors.confirmPassword?.message}
            </p>
          </div>

          {showVerificationField && (
            <input
              type="text"
              {...register("verificationCode")}
              placeholder={translatedText.verificationCodePlaceholder}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          )}

          <button
            type="submit"
            className="w-full bg-[#324B8E] text-white py-2 rounded-md font-semibold hover:bg-[#223b78] transition duration-200"
          >
            {translatedText.registerButton}
          </button>
        </form>

        <div className="text-sm text-gray-700 mt-6">
          <p className="mb-2">
            {translatedText.legalLine1}{" "}
            <a href="/conditions" className="text-cyan-600 underline">
              {translatedText.legalLine2}
            </a>{" "}
            {translatedText.legalLine3}{" "}
            <a href="/privacy" className="text-cyan-600 underline">
              {translatedText.legalLine4}
            </a>
            .
          </p>
          <hr className="my-2" />
          <p>
            {translatedText.alreadyHaveAccount}{" "}
            <a href="/login" className="text-blue-700 underline">
              {translatedText.loginLink}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
